/* globals window */

import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import SaveIcon from '@material-ui/icons/Save';
import SendIcon from '@material-ui/icons/Send';
import DoneIcon from '@material-ui/icons/Done';
import AddIcon from '@material-ui/icons/Add';
import qstring from 'query-string';
import { withRouter } from 'react-router-dom';
import { isEqual, cloneDeep } from 'lodash';
import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import VisibilityIcon from '@material-ui/icons/Visibility';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import QuoteSection from './components/QuoteSection';
import ConvertToAppointmentButton from './components/ConvertToAppointmentButton';
import { apiGet, apiPost, apiPut } from '../../brainApi';
import QuoteRequestInfo from './QuoteRequestInfo';
import ConfirmActionModal from '../../components/ConfirmActionModal';
import { QuickQuoteContext } from '../../Context';

const useStyles = makeStyles(() => ({
  container: {
    marginTop: '10px',
  },
  loadingContainer: {
    marginTop: '10px',
    paddingRight: '10px',
    alignItems: 'center',
  },
  loadingIcon: {
    marginRight: '10px',
  },
  quoteContainer: {
    marginTop: '10px',
    paddingRight: '10px',
  },
  headerContainerRight: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  quoteNumberText: {
    textAlign: 'right',
  },
  quoteNameInput: {
    fontSize: 50,
  },
  quoteSectionsContainer: {
    marginTop: '10px',
    width: '0px',
  },
  quoteRequestContainerExpanded: {
    marginTop: '10px',
  },
  quotePaper: {
    padding: '10px',
    width: '100%',
  },
  sectionsPaper: {
    padding: '10px',
  },
  addSectionButton: {
    display: 'flex',
    padding: '20px',
    marginTop: '20px',
    backgroundColor: 'lightgray',
    opacity: 0.5,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  buttonIcon: {
    marginLeft: '10px',
  },
  saveButton: {
    maxHeight: 45,
  },
  sendButton: {
    maxHeight: 45,
    marginLeft: '10px',
  },
  listIcon: {
    '& .MuiListItemIcon-root': {
      minWidth: 35,
    },
  },
  kebabMenu: {
    cursor: 'pointer',
    marginLeft: '10px',
  },
}));

const QuoteBuilder = ({ location }) => {
  const classes = useStyles();
  const { setVehicleData } = useContext(QuickQuoteContext);
  const [quote, setQuote] = useState({ name: '', note: '', sections: [] });
  const [savedQuote, setSavedQuote] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [quoteRequest, setQuoteRequest] = useState(null);
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [isEditable, setIsEditable] = useState(true);
  const [deletedSection, setDeletedSection] = useState(null);
  const [undoVisible, setUndoVisible] = useState(false);
  const [quoteRequestIsMinimized, setQuoteRequestIsMinimized] = useState(false);
  const [deleteQuoteModalOpen, setDeleteQuoteModalOpen] = useState(false);
  const [sendQuoteModalOpen, setSendQuoteModalOpen] = useState(false);
  const [isLoadingVehicle, setIsLoadingVehicle] = useState(false);
  const allChangesAreSaved = isEqual(quote, savedQuote);

  const [anchorEl, setAnchorEl] = useState(null);
  const kebabMenuOpen = Boolean(anchorEl);
  const handleClickKebabMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseKebabMenu = () => {
    setAnchorEl(null);
  };

  const validateCanSendQuote = () => {
    const isValid =
      quote?.sections?.length > 0 &&
      quote.sections.every(
        (s) =>
          !!s.options?.length &&
          s.options?.every((option) => !!option.services?.length),
      );

    return isValid;
  };

  const removeDeletedQuoteItems = (rawQuote) => ({
    ...rawQuote,
    sections: rawQuote.sections
      .filter((section) => !section.is_deleted)
      .map((section) => ({
        ...section,
        options: section.options.filter((option) => !option.is_deleted),
      })),
  });

  useEffect(() => {
    apiGet(`/quote-template`).then((allTemplates) => {
      setQuoteTemplates(allTemplates);
      getQuote(null, allTemplates);
    });
  }, []);

  const setQueryParams = (params) => {
    const queryParams = qstring.parse(location.search);
    const newQueryString = qstring.stringify({ ...queryParams, ...params });
    window.history.replaceState(
      {},
      '',
      `/#${location.pathname}?${newQueryString}`,
    );
  };

  useEffect(() => {
    if (quoteRequest?.vehicle_id) {
      setIsLoadingVehicle(true);
      apiGet(`/vehicle/${quoteRequest.vehicle_id}`)
        .then((v) => {
          setVehicleData(v);
          setIsLoadingVehicle(false);
        })
        .catch(() => setIsLoadingVehicle(false));
    }
  }, [quoteRequest?.vehicle_id]);

  const makeEmptySection = () => ({
    key: `new-section-${moment().valueOf()}`,
    name: null,
    note: null,
    options: [],
    quote_id: null,
    quote_section_id: null,
    quote_template_id: null,
    template: null,
  });

  const getQuote = (quoteId = null, templates = []) => {
    const queryParams = qstring.parse(location.search);
    const { request_id } = queryParams;
    const quote_id = quoteId || queryParams.quote_id;

    if (quote_id) {
      apiGet(`/quote/${quote_id}`).then((quoteJson) => {
        if (quoteJson.Status !== 'Failure') {
          let cleanQuote = removeDeletedQuoteItems(quoteJson);
          // If there are no sections and if there are multiple templates to choose from
          if (!cleanQuote?.sections?.length && templates?.length > 1)
            cleanQuote.sections = [makeEmptySection()];

          // If there's a quote request attached to this quote, use that instead of any query string param
          if (quoteJson.quote_request) {
            setQueryParams({
              quote_id,
              request_id: quoteJson.quote_request.id,
            });
            setQuoteRequest(quoteJson.quote_request);

            // Copy over note from quote request if quote does not have note
            if (!cleanQuote.admin_note && quoteJson.quote_request.admin_note) {
              cleanQuote = {
                ...cleanQuote,
                admin_note: quoteJson.quote_request.admin_note,
              };
            }
          }

          setQuote(cleanQuote);
          setSavedQuote(cloneDeep(cleanQuote));
          setIsEditable(cleanQuote.status === 'draft');
          setIsLoading(false);
        }
      });
    } else if (request_id) {
      apiGet(`/quote-request/${request_id}`).then((requestJson) => {
        if (requestJson.Status !== 'Failure' && quoteRequest === null) {
          setQuoteRequest(requestJson);
          // Copy over note from quote request if quote does not have note
          if (!quote.admin_note)
            setQuote({ ...quote, admin_note: requestJson.admin_note });
        }
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  };

  const confirmExitPage = (e) => {
    e.preventDefault();
    e.returnValue = '';
  };

  if (!allChangesAreSaved) {
    window.onbeforeunload = confirmExitPage;
  } else {
    window.onbeforeunload = null;
  }

  const updateSection = (index, items) => {
    const newQuote = cloneDeep(quote);
    newQuote.sections[index] = {
      ...newQuote.sections[index],
      ...items,
    };
    setQuote(newQuote);
  };

  const deleteSection = (index) => {
    const newQuote = cloneDeep(quote);
    setDeletedSection(newQuote.sections.splice(index, 1)[0]);
    setQuote(newQuote);
    setUndoVisible(true);
  };

  const onClickUndoDeleteSection = () => {
    const newQuote = cloneDeep(quote);
    newQuote.sections.push(deletedSection);
    setQuote(newQuote);
    setUndoVisible(false);
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setUndoVisible(false);
  };

  const onClickAddSection = () => {
    const newQuote = cloneDeep(quote);
    const newSections = newQuote.sections;
    const emptySection = makeEmptySection();
    newSections.push(emptySection);
    setQuote({ ...quote, sections: newSections });
  };

  const onClickSaveQuote = (callback = () => {}) => {
    setIsSaving(true);
    const quote_request = quoteRequest?.id
      ? { quote_request: quoteRequest }
      : {};
    apiPut('/quote-builder', {
      ...quote,
      ...quote_request,
      is_deleted: false,
    }).then(({ id }) => {
      setIsSaving(false);
      getQuote(id);
      callback(id);
    });
  };

  const sendQuote = () => {
    setIsSending(true);
    onClickSaveQuote((quote_id) =>
      apiPost('/send-quote', { quote_id }).then((resp) => {
        setIsSending(false);
        setSendQuoteModalOpen(false);
        if (resp.Status === 'success') {
          window.location.assign('/#/quote');
        } else {
          alert(
            'Sorry! There was an error sending this quote to the customer.',
          );
        }
      }),
    );
  };

  const deleteQuote = () => {
    setIsSaving(true);
    const quote_request = quoteRequest?.id
      ? { quote_request: quoteRequest }
      : {};
    apiPut('/quote-builder', {
      ...savedQuote,
      ...quote_request,
      is_deleted: true,
    }).then(() => {
      setIsSaving(false);
      window.location.assign('/#/quote');
    });
  };

  const onMinimizeQuoteRequest = (shouldMinimize) => {
    setQuoteRequestIsMinimized(shouldMinimize);
  };

  const SaveButtonIcon = () => {
    if (isSaving) return <CircularProgress size={20} />;
    if (allChangesAreSaved) return <DoneIcon />;
    return <SaveIcon />;
  };

  if (isLoading || isLoadingVehicle)
    return (
      <Grid container className={classes.loadingContainer}>
        <CircularProgress size={24} className={classes.loadingIcon} />
        <Typography variant="h1">Loading quote...</Typography>
      </Grid>
    );

  return (
    <div className={classes.container}>
      <Snackbar
        open={undoVisible}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        message="Section deleted!"
        action={
          <>
            <Button color="secondary" onClick={onClickUndoDeleteSection}>
              UNDO
            </Button>
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={() => setUndoVisible(false)}
            >
              <CloseIcon />
            </IconButton>
          </>
        }
      />

      <Grid container>
        <Grid
          container
          className={classes.quoteContainer}
          md={!quoteRequest || quoteRequestIsMinimized ? 12 : 8}
        >
          <Grid
            container
            style={{
              paddingRight:
                !quoteRequest || quoteRequestIsMinimized ? '10px' : '0px',
            }}
            md={quoteRequestIsMinimized ? 8 : 12}
          >
            <Paper className={classes.quotePaper}>
              <Grid container>
                <Grid md={9}>
                  <Grid>
                    {(!!quote?.id && (
                      <Typography variant="h2">Quote #{quote.id}</Typography>
                    )) || <Typography variant="h2">New Quote</Typography>}{' '}
                  </Grid>
                  <Grid md={8}>
                    {isEditable ? (
                      <Tooltip
                        title="Click to edit quote note"
                        placement="left"
                      >
                        <TextField
                          placeholder="Add a note..."
                          type="text"
                          onChange={(e) =>
                            setQuote({ ...quote, note: e.target.value })
                          }
                          value={quote.note}
                          InputProps={{
                            disableUnderline: true,
                          }}
                          label={null}
                          fullWidth
                          multiline
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="p">{quote.note}</Typography>
                    )}
                  </Grid>
                </Grid>
                <Grid
                  item
                  container
                  className={classes.headerContainerRight}
                  md={3}
                >
                  {isEditable && (
                    <Tooltip
                      title="Save your latest changes"
                      placement="bottom"
                    >
                      <Button
                        color="primary"
                        variant="contained"
                        size="small"
                        className={classes.saveButton}
                        onClick={() => onClickSaveQuote()}
                        startIcon={<SaveButtonIcon />}
                        disabled={isSaving || allChangesAreSaved}
                      >
                        {allChangesAreSaved ? 'Saved' : 'Save'}
                      </Button>
                    </Tooltip>
                  )}
                  {!!quote?.id && isEditable && (
                    <Tooltip
                      title="Send this quote to the customer"
                      placement="bottom"
                    >
                      <Button
                        color="primary"
                        variant="contained"
                        size="small"
                        className={classes.sendButton}
                        onClick={() => setSendQuoteModalOpen(true)}
                        startIcon={<SendIcon />}
                        disabled={!validateCanSendQuote()}
                      >
                        Send
                      </Button>
                    </Tooltip>
                  )}
                  {!isEditable && (
                    <Tooltip
                      title="This quote has already been sent to the customer"
                      placement="left-start"
                    >
                      <Typography variant="h4">Sent</Typography>
                    </Tooltip>
                  )}
                  {!!quote?.id && (
                    <MoreVertIcon
                      aria-label="more"
                      aria-haspopup="true"
                      className={classes.kebabMenu}
                      onClick={handleClickKebabMenu}
                    />
                  )}
                  <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={kebabMenuOpen}
                    onClose={handleCloseKebabMenu}
                    className={classes.listIcon}
                  >
                    {!!quote.booking_app_url && (
                      <MenuItem
                        onClick={() => {
                          handleCloseKebabMenu();
                          window.open(quote.booking_app_url);
                        }}
                      >
                        <ListItemIcon>
                          <VisibilityIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Preview Quote" />
                      </MenuItem>
                    )}
                    <ConvertToAppointmentButton
                      record={quote}
                      menuItemActionsBeforeOpening={(callback) => {
                        handleCloseKebabMenu();
                        onClickSaveQuote(callback);
                      }}
                    />
                    {isEditable && (
                      <MenuItem
                        onClick={() => {
                          handleCloseKebabMenu();
                          setDeleteQuoteModalOpen(true);
                        }}
                      >
                        <ListItemIcon>
                          <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Delete Entire Quote" />
                      </MenuItem>
                    )}
                  </Menu>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {!!quoteRequest && quoteRequestIsMinimized && (
            <Grid md={4}>
              <QuoteRequestInfo
                quoteRequestData={quoteRequest}
                quoteAnswers={quoteRequest.answers}
                isMinimizedDefault
                onMinimize={onMinimizeQuoteRequest}
                isEditable={isEditable}
                quote={quote}
              />
            </Grid>
          )}

          <Grid className={classes.quoteSectionsContainer} md={12}>
            <Paper className={classes.sectionsPaper}>
              {quote.sections.map((section, index) => (
                <QuoteSection
                  key={section.id}
                  sectionIndex={index}
                  section={section}
                  isEditable={isEditable}
                  updateSection={(items) => updateSection(index, items)}
                  deleteSection={() => deleteSection(index)}
                  quoteTemplates={quoteTemplates}
                />
              ))}
              {isEditable && <AddSectionButton onClick={onClickAddSection} />}
            </Paper>
          </Grid>
        </Grid>

        {!!quoteRequest && !quoteRequestIsMinimized && (
          <Grid className={classes.quoteRequestContainerExpanded} md={4}>
            <QuoteRequestInfo
              quoteRequestData={quoteRequest}
              quoteAnswers={quoteRequest.answers}
              isMinimizedDefault={false}
              onMinimize={onMinimizeQuoteRequest}
              isEditable={isEditable}
              quote={quote}
            />
          </Grid>
        )}
      </Grid>
      <ConfirmActionModal
        open={deleteQuoteModalOpen}
        mainText="Are you sure you want to delete this whole quote?"
        confirmText="Delete Quote"
        onConfirm={deleteQuote}
        onCancel={() => setDeleteQuoteModalOpen(false)}
      />
      <ConfirmActionModal
        open={sendQuoteModalOpen}
        mainText={`Are you sure you're ready to send this quote to ${
          quoteRequest?.customer_id
            ? 'the customer'
            : quoteRequest?.customer_email
        }?`}
        confirmText={isSending ? 'Sending...' : 'Send Quote'}
        onConfirm={isSending ? () => {} : sendQuote}
        cancelText={isSending ? '' : 'Cancel'}
        onCancel={isSending ? () => {} : () => setSendQuoteModalOpen(false)}
        maxWidth={false}
      />
    </div>
  );
};

const AddSectionButton = ({ onClick }) => {
  const classes = useStyles();
  return (
    <Paper className={classes.addSectionButton} onClick={onClick}>
      <AddIcon fontSize="large" />
      <Typography variant="p">Add New Section</Typography>
    </Paper>
  );
};

export default withRouter(QuoteBuilder);
